import React from "react";
import { cn } from "@semo/shared-ui";
import { DocumentRequestCard } from "../cards/DocumentRequestCard";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";
import {
  ContractDocumentManagementCard,
  type ContractDocumentItem,
  type ContractRestrictionConfirmSnapshot,
} from "../cards/ContractDocumentManagementCard";
import type { ChatMessageProps } from "../types";
import type { DocumentRequestMessageResponseDto } from "@semo/api-contracts";
import type { LocalMessageState } from "../../types/message";
import { useChatActions } from "../../context/ChatActionContext";

interface DocumentRequestMessageProps extends Omit<
  ChatMessageProps,
  "message"
> {
  message: DocumentRequestMessageResponseDto & LocalMessageState;
  sender: "me" | "other";
  time: string;
}

/**
 * DOCUMENT_REQUEST 메시지 (계약서류 카드)
 *
 * 상태에 따라 두 모드로 렌더:
 * - status='pending': 기존 pill 선택 카드 (buyer 전용). supplier 에게는 미노출.
 * - status='requested': [계약 서류 관리] 마스터 카드. buyer/supplier 모두 노출.
 *
 * 서류를 요청하는 주체는 발주기관이므로 발주기관 쪽에 붙인다 — 발주기관 화면에서는
 * 오른쪽, 공급사 화면에서는 왼쪽. 가운데에 두면 누가 보낸 것인지가 사라져서
 * 시스템 안내처럼 읽힌다.
 * 폭은 다른 문서형 카드와 같은 상한(document)을 따른다. 채팅 폭을 끝까지 채우면
 * 주변 말풍선/카드와 축이 안 맞아 대화에서 떨어져 나온 것처럼 보인다.
 */
export const DocumentRequestMessage: React.FC<DocumentRequestMessageProps> = ({
  message,
  role,
}) => {
  const { onRequestContractDocuments, onOpenCmarketContracts } =
    useChatActions();

  const data = message.documentRequestData;
  const status = data.status;

  // 선택 카드(pending)는 buyer 전용
  if (status === "pending" && role === "supplier") return null;
  if (!data.documents || data.documents.length === 0) return null;

  const contractDocuments = (
    data as unknown as {
      contractDocuments?: ContractDocumentItem[];
    }
  ).contractDocuments;
  // 계약의 결제 축 (backend 가 contracts 를 그때그때 읽어 채운다).
  // paymentChannel 이 null 이면 카드가 결제 CTA 를 그리지 않는다 — 레거시 선결제 계약이
  // 여기 해당한다(payment_channel 컬럼 신설 2026-08-04 이전).
  const { paymentChannel, isPaid, taxInvoiceStatus } = data as unknown as {
    paymentChannel?: "cash" | "card" | null;
    isPaid?: boolean;
    taxInvoiceStatus?: "REQUESTED" | "ISSUED" | "CANCELLED" | null;
  };
  // paperCode 135 제출 스냅샷 — "예" 응답이 있으면 카드가 경고 배너를 그린다.
  const restrictionConfirm = (
    data as unknown as {
      restrictionConfirm?: ContractRestrictionConfirmSnapshot;
    }
  ).restrictionConfirm;

  return (
    <div
      className={cn(
        "flex w-full px-1 sm:px-0 mb-4",
        role === "supplier" ? "justify-start" : "justify-end",
      )}
    >
      <MessageCardWrapper size="document">
        {status === "requested" ? (
          <ContractDocumentManagementCard
            roomId={message.roomId}
            role={role}
            items={contractDocuments ?? []}
            contractId={data.contractId}
            paymentChannel={paymentChannel ?? null}
            isPaid={isPaid ?? false}
            taxInvoiceStatus={taxInvoiceStatus ?? null}
            onOpenCmarketContracts={onOpenCmarketContracts}
            restrictionConfirm={restrictionConfirm}
          />
        ) : (
          <DocumentRequestCard
            documents={data.documents}
            role={role}
            status={data.status}
            issuedPaperCodes={data.issuedPaperCodes}
            onRequestDocuments={
              onRequestContractDocuments
                ? (paperCodes, idempotencyKey) =>
                    onRequestContractDocuments(
                      message.id,
                      paperCodes,
                      idempotencyKey,
                    )
                : undefined
            }
          />
        )}
      </MessageCardWrapper>
    </div>
  );
};
